<template>
  <div class="container">
    <div class="flex flex-wrap md:items-center lg:mt-4">
      <div class="hidden md:block text-center w-full md:order-2 md:w-1/3 lg:w-1/6">
        <span class="block text-center md:text-right dark:text-white">
          {{ $t('components.vips-filter.found', totalVips) }}
        </span>
      </div>

      <div class="w-full flex flex-wrap md:order-1 md:w-2/3 lg:w-5/6 relative z-10 items-center md:space-x-2 lg:space-x-3">
        <button
          type="button"
          class="w-full border-2 border-black dark:border-white block text-center text-sm font-bold py-3 rounded-lg md:w-auto md:px-8 md:mt-0 hover:bg-gray-100 bg-white dark:bg-gray-900 dark:text-white"
          @click="open = true"
        >
          {{ __('components.vips-filter.title') }}
          <span
            v-show="selectedFilters.length > 0"
            class="rounded-full bg-red text-white text-[10px] inline-block w-[15px] h-[15px] text-center leading-[1.6] ml-1"
          >
            {{ selectedFilters.length }}
          </span>
        </button>

        <button
          v-for="(selectedFilter, i) in selectedFilters"
          :key="`selected-filter-${i}`"
          class="text-xs border border-gray-300 dark:border-white text-gray-300 dark:text-white py-2 px-3 rounded-md hidden md:block dark:bg-gray-900"
          @click="selectedFilter.remove"
        >
          {{ selectedFilter.title }}

          <img
            src="/frontend/assets/cancel-black.svg"
            class="inline ml-1"
          >
        </button>
      </div>
    </div>
  </div>

  <TransitionRoot
    as="template"
    :show="open"
  >
    <DialogComponent
      as="div"
      class="relative z-50"
      @close="close"
    >
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      </TransitionChild>

      <div class="fixed z-50 inset-0 overflow-y-auto">
        <div class="flex items-end sm:items-center justify-center min-h-full p-4 sm:p-0">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel class="bg-white fixed bottom-0 left-0 right-0 rounded-t-3xl p-6 md:left-[5%] md:right-[5%] lg:right-[15%] lg:left-[15%] md:bottom-auto md:top-1/2 md:transform md:-translate-y-1/2 md:rounded-3xl lg:px-12 lg:py-6 max-h-full overflow-y-scroll">
              <div class="flex flex-wrap items-center">
                <div class="w-1/6 lg:order-3 lg:flex lg:justify-end">
                  <button
                    type="button"
                    @click="close"
                  >
                    <img src="/frontend/assets/close-filter.svg">
                  </button>
                </div>

                <div class="w-4/6 text-center text-2xl font-bold lg:order-2 lg:text-4xl">
                  {{ __('components.vips-filter.title') }}
                </div>

                <div class="w-1/6 text-right lg:order-1">
                  <button
                    type="button"
                    class="lg:hidden"
                    @click="resetFilter"
                  >
                    {{ __('components.vips-filter.reset') }}
                  </button>
                </div>
              </div>

              <div class="flex flex-wrap lg:mt-2 max-h-full overflow-auto">
                <div class="w-full md:w-1/2 md:pr-3">
                  <div class="mt-6">
                    <span class="text-xl font-bold mb-2 block">
                      {{ __('components.vips-filter.sort') }}
                    </span>

                    <div class="text-lg space-y-2.5 all-vips-filter-buttons">
                      <button
                        v-for="(name, key) in sortingCategories"
                        :key="`sorting-category-${key}`"
                        type="button"
                        class="block hover:underline"
                        :class="{ 'active underline': form.selectedSortingCategory === key }"
                        @click="form.selectedSortingCategory = form.selectedSortingCategory !== key ? key : null;"
                      >
                        {{ name }}
                      </button>
                    </div>
                  </div>
                </div>

                <div class="w-full my-6 md:w-1/2 md:pl-3">
                  <!-- eslint-disable vue/no-v-html -->
                  <span
                    class="text-xl font-bold mb-2 block"
                    v-html="__('components.vips-filter.categories')"
                  />
                  <!-- eslint-enable vue/no-v-html -->

                  <div class="grid grid-cols-2 gap-2 all-vips-filter-cat">
                    <button
                      v-for="category in categories"
                      :key="`filter-category-${category.slug}`"
                      class="text-center w-full rounded-xl text-lg py-4"
                      :class="form.selectedCategory === category.slug ? 'bg-red text-white' : 'bg-white text-black border border-black'"
                      @click="form.selectedCategory = form.selectedCategory !== category.slug ? category.slug : null;"
                    >
                      {{ category.name }}

                      <img
                        v-show="form.selectedCategory === category.slug"
                        src="/frontend/assets/check-white.svg"
                        class="inline ml-1"
                      >
                    </button>
                  </div>
                </div>

                <div class="w-full md:mt-8 lg:text-center">
                  <button
                    type="button"
                    class="button-orange shadow-button-black block w-full lg:inline-block lg:w-auto lg:px-24"
                    @click="submitFilter(true)"
                  >
                    <span v-if="totalVips > 0">
                      {{ $t('components.vips-filter.found-dialog', totalVips) }}
                    </span>
                    <span v-else>
                      {{ $t('components.vips-filter.nothing-found', totalVips) }}
                    </span>
                  </button>

                  <button
                    type="button"
                    class="hidden lg:block lg:mt-4 lg:mx-auto hover:underline"
                    @click="resetFilter"
                  >
                    {{ __('components.vips-filter.reset-dialog') }}
                  </button>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </DialogComponent>
  </TransitionRoot>
</template>

<script>
import { Dialog as DialogComponent, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue';
import {cloneDeep} from 'lodash';
import {ref} from 'vue';

export default {
  components: {
    DialogComponent,
    DialogPanel,
    TransitionChild,
    TransitionRoot,
  },

  props: {
    search: {
      type: String,
      required: true,
    },

    totalVips: {
      type: Number,
      required: true,
    },

    occasions: {
      type: Array,
      required: true,
    },

    categories: {
      type: Array,
      required: true,
    },

    initialFilter: {
      type: Object,
      required: false,
      default: () => {},
    },
  },

  emits: ['update:open', 'update:search', 'filter:reset', 'filter:recalculate', 'filter:submit'],

  setup(props) {
    let form = ref({
      selectedSortingCategory: null,
      selectedOccasion: null,
      selectedCategory: null,
    });

    if (Object.hasOwnProperty.call(props.initialFilter, 'sort')) {
      form.value.selectedSortingCategory = ref(props.initialFilter.sort).value;
    }
    if (Object.hasOwnProperty.call(props.initialFilter, 'occasion')) {
      form.value.selectedOccasion = ref(props.initialFilter.occasion).value;
    }
    if (Object.hasOwnProperty.call(props.initialFilter, 'category')) {
      form.value.selectedCategory = ref(props.initialFilter.category).value;
    }

    return {form};
  },

  data() {
    return {
      open: false,

      sortingCategories: {
        popular: 'Meest gekozen',
        newest: 'Nieuwste VIPS',
      },

      filter: cloneDeep(this.initialFilter),
    };
  },

  computed: {
    selectedFilters() {
      const filters = [];

      if (this.search && this.search.length > 0) {
        filters.push({
          title: `Zoeken: "${this.search}"`,
          remove: () => {
            this.$emit('update:search', '');
            this.$nextTick(() => this.submitFilter());
          },
        });
      }
      if ('occasion' in this.filter) {
        filters.push({
          title: this.occasions.find((occasion) => this.filter.occasion === occasion.slug).short_name,
          remove: () => {
            this.form.selectedOccasion = null;
            this.submitFilter();
          },
        });
      }
      if ('category' in this.filter) {
        filters.push({
          title: this.categories.find((category) => this.filter.category === category.slug).name,
          remove: () => {
            this.form.selectedCategory = null;
            this.submitFilter();
          },
        });
      }
      if ('sort' in this.filter) {
        filters.push({
          title: this.sortingCategories[this.filter.sort],
          remove: () => {
            this.form.selectedSortingCategory = null;
            this.submitFilter();
          },
        });
      }

      return filters;
    },
  },

  watch: {
    form: {
      deep: true,
      handler: function() { this.recalculateFilter(); },
    },
  },

  methods: {
    close() {
      this.open = false;
    },

    buildFilter() {
      const filter = {};

      if (this.search.length > 0) {
        filter.search = this.search;
      }
      if (this.form.selectedSortingCategory !== null) {
        filter.sort = this.form.selectedSortingCategory;
      }
      if (this.form.selectedOccasion !== null) {
        filter.occasion = this.form.selectedOccasion;
      }
      if (this.form.selectedCategory !== null) {
        filter.category = this.form.selectedCategory;
      }

      this.filter = filter;
    },

    resetFilter() {
      this.form.selectedOccasion = null;
      this.form.selectedCategory = null;
      this.form.selectedSortingCategory = null;
      this.filter = {};

      this.close();
      this.$emit('filter:reset');
    },

    recalculateFilter() {
      this.buildFilter();

      this.$emit('filter:recalculate', this.filter);
    },

    submitFilter(close = false, initial = false) {
      this.buildFilter();

      if (close) {
        this.close();
      }

      this.$emit('filter:submit', this.filter, initial);
    },
  },
};
</script>
